import { z } from 'zod';
import { JOB_NEGOTIATION_TERM_KEYS } from '~/features/market/negotiations/utils';
import { OpportunityStatusSchema } from '~/features/provider-hlx/job-opportunities/schemas';
import { ResponseUnmaskStateSchema } from '~/features/shared/market-content/responseUnmask.api';
import { JobResponseAvailableActionsSchema, JobResponseTurnSchema } from '~/types/hlx/jobResponse';

export const ServiceOfferSelectionSchema = z.object({
  date: z.iso.date(),
  detailMode: z.enum(['DATE_ONLY', 'SHIFT_DETAIL']),
  startsAt: z.iso.datetime({ offset: true }).nullable(),
  endsAt: z.iso.datetime({ offset: true }).nullable(),
  timezone: z.string(),
  shiftLabel: z.enum(['DAY', 'EVENING', 'NIGHT', 'CUSTOM']).nullable(),
  shiftType: z.enum(['SHIFT', 'CALL']).nullable(),
  workLocation: z.enum(['REMOTE', 'ONSITE', 'PARTIAL_REMOTE']).nullable(),
  stateCode: z.string().nullable(),
  rateOverrideType: z.enum(['HOUR', 'SHIFT', 'RVU']).nullable(),
  pricePerHourOverride: z.string().nullable(),
  pricePerShiftOverride: z.string().nullable(),
  pricePerCallShiftOverride: z.string().nullable(),
});

export const ServiceCounterAvailabilitySchema = z.object({
  responseId: z.number().int().positive(),
  expectedNegotiationId: z.number().int().positive(),
  entries: z
    .array(
      ServiceOfferSelectionSchema.extend({
        availabilityEntryId: z.number().int().positive(),
        currentlySelected: z.boolean(),
      })
    )
    .refine(
      (entries) =>
        new Set(entries.map((entry) => entry.availabilityEntryId)).size === entries.length
    ),
});

export const ServiceCounterInputSchema = z.strictObject({
  expectedNegotiationId: z.number().int().positive(),
  availabilityEntryIds: z
    .array(z.number().int().positive())
    .nonempty()
    .refine((ids) => new Set(ids).size === ids.length),
  negotiationTerms: z.record(
    z.enum(JOB_NEGOTIATION_TERM_KEYS),
    z.union([z.string(), z.number().int(), z.null()])
  ),
  messageBody: z.string().refine((body) => body.trim().length > 0),
});

export type ServiceCounterInput = z.infer<typeof ServiceCounterInputSchema>;
export type ServiceCounterAvailability = z.infer<typeof ServiceCounterAvailabilitySchema>;

export const ServiceOfferSchema = z
  .object({
    id: z.number().int().positive(),
    version: z.number().int().positive(),
    sender: z.enum(['EMPLOYER', 'PROVIDER']),
    createdAt: z.iso.datetime({ offset: true }).nullish(),
    pricePerHour: z.string().nullable(),
    startDate: z.string().nullable(),
    endDate: z.string().nullable(),
    terms: z
      .record(z.string(), z.union([z.string(), z.number().int(), z.null()]))
      .refine(
        (terms) =>
          [...JOB_NEGOTIATION_TERM_KEYS, 'totalContractValue', 'avgHourlyRate'].every((key) =>
            Object.prototype.hasOwnProperty.call(terms, key)
          ),
        { message: 'Complete offer terms are required.' }
      ),
    selectionStatus: z.enum(['RECORDED', 'UNAVAILABLE']),
    availabilitySelection: z.array(ServiceOfferSelectionSchema),
  })
  .refine(
    (offer) => (offer.selectionStatus === 'RECORDED') === offer.availabilitySelection.length > 0,
    {
      message: 'Offer selection is incomplete.',
    }
  );

export type ServiceOffer = z.infer<typeof ServiceOfferSchema>;

export const ServiceOpportunitySchema = z
  .object({
    responseId: z.number().int().positive(),
    viewerRole: z.enum(['RESPONDER', 'LISTING_OWNER']),
    status: OpportunityStatusSchema,
    isUnmasked: z.boolean(),
    appliedAt: z.string().nullable(),
    updatedAt: z.string(),
    listing: z.object({
      id: z.number().int().positive(),
      title: z.string(),
      listingKind: z.string(),
      workLocation: z.string(),
    }),
    counterparty: z.object({
      displayName: z.string(),
      masked: z.boolean(),
      providerId: z.number().int().positive().nullable(),
    }),
    latestNegotiation: ServiceOfferSchema.nullable(),
    negotiationHistory: z.array(ServiceOfferSchema).optional(),
    unreadCount: z.number().int().nonnegative(),
    unmaskState: ResponseUnmaskStateSchema,
    turn: JobResponseTurnSchema,
    availableActions: JobResponseAvailableActionsSchema,
  })
  .refine(
    (value) =>
      value.responseId === value.unmaskState.responseId &&
      value.isUnmasked === value.unmaskState.isUnmasked &&
      value.counterparty.masked !== value.isUnmasked,
    { message: 'Service response privacy scope changed.' }
  );

export const ServiceOpportunitiesPageSchema = z.object({
  data: z.array(ServiceOpportunitySchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});

export type ServiceOpportunity = z.infer<typeof ServiceOpportunitySchema>;
export type ServiceOpportunitiesPage = z.infer<typeof ServiceOpportunitiesPageSchema>;
